import { useCallback } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import { RECALCULATE_STOP_SERVICE_TIMES } from '@/graphql/mutations/stops';
import { STOPS_FOR_ORDER } from '@/graphql/queries/stops';

// After a routed order's services change, null the containing stops' service_time and flag their
// routes for re-optimize, so the next route map recomputes dwell from the order's current services.
// Returns true when the stops are known to be invalidated (including the no-op cases: no order, or
// the order isn't on a stop), false when the lookup or the mutation failed — the service edit may
// already be saved against a stale stop.service_time, so callers must surface or retry the failure
// rather than treating a resolved promise as success. No `onError` on the mutation on purpose: it
// would swallow the rejection and make failures indistinguishable from success.
export const useRecalcStopServiceTimes = (order) => {
    const client = useApolloClient();
    const [recalcStops] = useMutation(RECALCULATE_STOP_SERVICE_TIMES);

    return useCallback(async () => {
        const order_id = order?.order_id;
        if (!order_id) return true;
        try {
            const { data } = await client.query({
                query: STOPS_FOR_ORDER,
                variables: { order_id },
                fetchPolicy: 'network-only',
            });
            const stops = data?.stops || [];
            if (stops.length === 0) return true;
            const stopIds = stops.map((s) => s.stop_id);
            const routeIds = [...new Set(stops.map((s) => s.route_id).filter(Boolean))];
            await recalcStops({ variables: { stop_ids: stopIds, route_ids: routeIds } });
            return true;
        } catch (e) {
            console.error(e);
            captureException(e);
            return false;
        }
    }, [order?.order_id, client, recalcStops]);
};
